import { Skeleton, Stack } from '@mui/material';
import React from 'react';
import Scrollbar from 'src/components/scrollbar/Scrollbar';
import { useResponsiveBreakpoints } from 'src/hooks/useResponsiveBreakpoints';

interface VisitCalendarSkeletonProps {
  isQuickBooking?: boolean;
  numberOfSkeletonRows?: number;
  calendarColumns?: number;
}

const VisitCalendarSkeleton: React.FC<VisitCalendarSkeletonProps> = ({
  isQuickBooking = false,
  numberOfSkeletonRows = 5,
  calendarColumns,
}) => {
  const { isTabletSm, isTabletLg } = useResponsiveBreakpoints();

  const daysLength = calendarColumns || (isTabletSm ? 2 : isTabletLg ? 3 : 4);

  return (
    <>
      <Stack
        sx={{
          flex: 1,
          position: 'relative',
          height: '100%',
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          flex={1}
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
        >
          {/* Left Navigation Button Skeleton */}
          <Skeleton
            variant="circular"
            width={40}
            height={40}
          />

          {/* Calendar Content */}
          <Stack
            direction="column"
            gap={2}
            flex={1}
            sx={{
              mx: { md: 0, lg: 2 },
              width: 'calc(100% - 128px)',
              '@media (max-width:475px)': {
                width: 'calc(100% - 88px)',
                mx: 2,
              },
              '@media (max-width:360px)': {
                width: 'calc(100% - 128px)',
              },
              '@media (max-width:330px)': {
                width: 'calc(100% - 108px)',
              },
              '@media (max-width:300px)': {
                width: 'calc(100% - 78px)',
              },
              height: '100%',
            }}
          >
            {/* Day Headers */}
            <Stack flexDirection="row" justifyContent="space-between">
              {Array.from({ length: daysLength }).map((_, i) => (
                <Stack
                  sx={{
                    width: isQuickBooking ? '71px' : '83px',
                    '@media (max-width:475px)': {
                      width: '66px',
                    },
                  }}
                  key={i}
                >
                  <Stack>
                    {/* Day name skeleton */}
                    <Skeleton
                      variant="text"
                      width="100%"
                      height={20}
                      sx={{ textAlign: 'center', mb: 0.5 }}
                    />
                    {/* Date skeleton */}
                    <Skeleton
                      variant="text"
                      width="80%"
                      height={16}
                      sx={{ mx: 'auto' }}
                    />
                  </Stack>
                </Stack>
              ))}
            </Stack>

            {/* Time Slots */}
            <Scrollbar
              sx={{
                flex: 1,
                height: '100%',
              }}
            >
              <Stack
                flexDirection="row"
                flex={1}
                justifyContent="space-between"
                position="relative"
              >
                {Array.from({ length: daysLength }).map((_, i) => (
                  <Stack
                    spacing={2}
                    key={i}
                    sx={{
                      width: isQuickBooking ? '71px' : '83px',
                      '@media (max-width:475px)': {
                        width: '66px',
                      },
                      minHeight: '40px',
                    }}
                  >
                    <Stack gap={1}>
                      {Array.from({ length: numberOfSkeletonRows }).map((_, j) => (
                        <Skeleton
                          key={j}
                          variant="rounded"
                          sx={{
                            width: isQuickBooking ? '71px' : '83px',
                            height: '32px',
                            '@media (max-width:475px)': {
                              width: '66px',
                            },
                          }}
                        />
                      ))}
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </Scrollbar>
          </Stack>

          {/* Right Navigation Button Skeleton */}
          <Skeleton
            variant="circular"
            width={40}
            height={40}
          />
        </Stack>
      </Stack>
    </>
  );
};

export default VisitCalendarSkeleton;
